'use client'

// React Imports
import { useState } from 'react'

// MUI Imports
import Card from '@mui/material/Card'
import Grid from '@mui/material/Grid'
import { Button, Typography, TextField, CircularProgress, Box } from '@mui/material'

import type { SubmitHandler } from 'react-hook-form'

// Third-party Imports
import { toast } from 'react-toastify'
import { Controller, useForm } from 'react-hook-form'
import { valibotResolver } from '@hookform/resolvers/valibot'
import { email, object, minLength, string, pipe, nonEmpty } from 'valibot'
import type { InferInput } from 'valibot'

import { authFetch } from '@/utils/authFetch'

type FormData = InferInput<typeof schema>

interface TotpVerifyComponentProps {
  redirectToSignIn?: () => void
}

const schema = object({
  firstName: pipe(
    string(),
    nonEmpty('This field is required'),
    minLength(3, 'First Name must be at least 3 characters long')
  ),
  lastName: pipe(
    string(),
    nonEmpty('This field is required'),
    minLength(3, 'Last Name must be at least 3 characters long')
  ),
  email: pipe(string(), minLength(1, 'This field is required'), email('Please enter a valid email address')),
  organization: pipe(
    string(),
    nonEmpty('This field is required'),
    minLength(3, 'Organization must be at least 3 characters long')
  )
})

const Register: React.FC<TotpVerifyComponentProps> = ({ redirectToSignIn }) => {
  const [isLoading, setLoading] = useState<boolean>(false) // Proper typing

  // Hooks
  const {
    control,
    reset,
    handleSubmit,
    formState: { errors }
  } = useForm<FormData>({
    resolver: valibotResolver(schema),
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      organization: ''
    }
  })

  const onSubmit: SubmitHandler<FormData> = async (data: FormData) => {
    try {
      setLoading(true)

      const response = await authFetch(`/api/register`, {
        method: 'POST',
        body: JSON.stringify(data)
      })

      if (response.error) {
        toast.error(response.error, { autoClose: 5000 })
      } else {
        toast.success('Account created successfully! Please wait for admin activation.', { autoClose: 5000 })
        reset()
      }
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'An account with this email already exists', {
        autoClose: 5000
      })
      console.error('An account with this email already exists', error)
    } finally {
      setLoading(false) // Single call in finally block
    }
  }

  return (
    <Box position='relative'>
      <Card className='shadow-none'>
        <div>
          <Typography variant='h5'>{`Welcome! 👋🏻`}</Typography>
          <Typography className='mb-4'>Register new account</Typography>
        </div>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Grid container spacing={5}>
            <Grid item xs={12}>
              <Controller
                name='firstName'
                control={control}
                rules={{ required: true }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label='First Name'
                    placeholder='John'
                    disabled={isLoading}
                    {...(errors.firstName && { error: true, helperText: errors.firstName.message })}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Controller
                name='lastName'
                control={control}
                rules={{ required: true }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label='Last Name'
                    placeholder='Doe'
                    disabled={isLoading}
                    {...(errors.lastName && { error: true, helperText: errors.lastName.message })}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Controller
                name='email'
                control={control}
                rules={{ required: true }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    type='email'
                    label='Email'
                    placeholder='johndoe@company.com'
                    disabled={isLoading}
                    {...(errors.email && { error: true, helperText: errors.email.message })}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Controller
                name='organization'
                control={control}
                rules={{ required: true }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label='Organization'
                    placeholder='Mirror'
                    disabled={isLoading}
                    {...(errors.organization && { error: true, helperText: errors.organization.message })}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} className='flex gap-4 mb-4'>
              <Button variant='contained' fullWidth type='submit' disabled={isLoading}>
                {isLoading ? 'Registering...' : 'Register'}
              </Button>

              <Button variant='outlined' type='reset' onClick={() => reset()} disabled={isLoading}>
                Reset
              </Button>
            </Grid>
          </Grid>
        </form>
        <div>
          <Typography className='text-end' color='primary'>
            <Button
              variant='text'
              fullWidth
              sx={{ justifyContent: 'flex-center' }}
              onClick={() => redirectToSignIn?.()}
              disabled={isLoading}
            >
              Sign In
            </Button>
          </Typography>
        </div>
      </Card>

      {isLoading && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 5,
            borderRadius: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center', // Center the spinner
            transition: 'all' // Smooth appearance
          }}
        >
          <CircularProgress
            size={40}
            sx={{
              filter: 'drop-shadow(0 0 3px rgba(255,255,255,0.7))'
            }}
          />
        </Box>
      )}
    </Box>
  )
}

export default Register
